import React, { useState, useEffect } from 'react';
import './profile.scss';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  updateDoc,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/navbar/Navbar';
import Sidebar from '../../components/sidebar/Sidebar';
import Loading from '../../components/Loading/Loading';
import { sendPasswordResetEmail } from 'firebase/auth';

const Profile = ({ isReadOnly, userRole }) => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [currentStaff, setCurrentStaff] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // State to track selected users
  const [staffs, setStaffs] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [cardFilter, setCardFilter] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    if (auth.currentUser) {
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      getDoc(userDocRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            setCurrentUser(docSnap.data());
          }
        })
        .catch((error) => {
          console.error('Error fetching user:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);

  useEffect(() => {
    if (auth.currentUser) {
      const staffDocRef = doc(db, 'staff', auth.currentUser.uid);
      getDoc(staffDocRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            setCurrentStaff(docSnap.data());
          }
        })
        .catch((error) => {
          console.error('Error fetching staff:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const usersCollection = collection(db, 'users');
    const adminLogsCollection = collection(db, 'adminLogs');

    const pendingUsersQuery = query(
      usersCollection,
      where('status', '==', 'pending')
    );
    const pendingUnsubscribe = onSnapshot(pendingUsersQuery, (snapshot) => {
      if (!isMounted) return;
      const users = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        if (data.timeStamp?.toDate) {
          data.formattedDate = data.timeStamp.toDate().toLocaleDateString();
          data.formattedTime = data.timeStamp.toDate().toLocaleTimeString();
        }
        return { id: docSnap.id, ...data };
      });
      setPendingUsers(users);
    });

    const staffsQuery = query(collection(db, 'staff'));
    const staffUnsubscribe = onSnapshot(staffsQuery, (snapshot) => {
      setStaffs(
        snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      );
    });

    const logQuery = query(adminLogsCollection, orderBy('timestamp', 'desc'));
    const logUnsubscribe = onSnapshot(logQuery, (snapshot) => {
      setAdminLogs(
        snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      );
    });

    return () => {
      isMounted = false;
      pendingUnsubscribe();
      staffUnsubscribe();
      logUnsubscribe();
    };
  }, []);

  const filteredPendingUsers = pendingUsers.filter(
    (user) =>
      (user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.membershipCode
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())) &&
      user.phone?.toLowerCase().includes(phoneFilter.toLowerCase()) &&
      user.card?.toLowerCase().includes(cardFilter.toLowerCase())
  );

  const displayedPendingUsers = filteredPendingUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePasswordReset = (email) => {
    if (!hasPermission('reset-password')) {
      alert("You don't have permission to reset passwords");
      return;
    }

    sendPasswordResetEmail(auth, email)
      .then(() => {
        alert('Password reset email sent!');
      })
      .catch((error) => {
        console.error('Error resetting password:', error);
      });
  };

  const handleApproveUser = (userId) => {
    if (!hasPermission('approve-users')) {
      alert("You don't have permission to approve users");
      return;
    }

    const userRef = doc(db, 'users', userId);
    updateDoc(userRef, { status: 'approved' })
      .then(() => {
        alert('User approved successfully.');
      })
      .catch((error) => {
        console.error('Error approving user:', error);
        alert('Failed to approve user. Please try again.');
      });
  };

  const handleApproveSelectedUsers = () => {
    if (isReadOnly) {
      alert("You don't have permission to approve users");
      return;
    }

    selectedUsers.forEach((userId) => {
      handleApproveUser(userId);
    });
    setSelectedUsers([]);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.includes(userId)
        ? prevSelected.filter((id) => id !== userId)
        : [...prevSelected, userId]
    );
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === displayedPendingUsers.length) {
      setSelectedUsers([]); // Deselect all if already selected
    } else {
      setSelectedUsers(displayedPendingUsers.map((user) => user.id)); // Select all users on the current page
    }
  };

  const handleAddUser = () => {
    navigate('/add-user');
  };

  // Function to check if user has permission for specific actions
  const hasPermission = (action) => {
    // Audit and compliance roles are always read-only
    if (['audit', 'compliance'].includes(userRole)) {
      return false;
    }

    switch (action) {
      case 'reset-password':
        return userRole === 'super-user';
      case 'approve-users':
        return userRole === 'super-user';
      case 'add-user':
        return userRole === 'super-user';
      case 'bulk-approve':
        return userRole === 'super-user';
      default:
        return false;
    }
  };

  if (isLoading) {
    return <Loading inline={true} message="Loading profile data..." />;
  }

  return (
    <div className="profile">
      <Sidebar />
      <div className="profileContainer">
        <Navbar />
        <div className="profileContent">
          {isReadOnly && (
            <div className="readOnlyBanner">
              You are in read-only mode ({userRole})
            </div>
          )}
          <div className="adminsSection">
            <h2>List of Staffs</h2>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {staffs.map((staff) => (
                  <tr key={staff.id}>
                    <td>{staff.name}</td>
                    <td>{staff.email}</td>
                    <td>{staff.role}</td>
                    <td>
                      {hasPermission('reset-password') && (
                        <button
                          onClick={() => handlePasswordReset(staff.email)}
                          className={isReadOnly ? 'disabled' : ''}
                        >
                          Reset Password
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="searchSection">
            <h1>Search Pending For User: </h1>
            <input
              type="text"
              placeholder="Search by name, code or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="searchField"
            />
            <i className="searchIcon">🔍</i>
          </div>
          <div className="usersSection">
            <h2>List of Pending Shareholders</h2>
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        selectedUsers.length === displayedPendingUsers.length
                      }
                      onChange={handleSelectAllUsers}
                    />
                  </th>
                  <th>Username</th>
                  <th>
                    Contact Number
                    <div className="columnFilter">
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={phoneFilter}
                        onChange={(e) => setPhoneFilter(e.target.value)}
                        className="filterInput"
                      />
                    </div>
                  </th>
                  <th>
                    Ghana Card Number
                    <div className="columnFilter">
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={cardFilter}
                        onChange={(e) => setCardFilter(e.target.value)}
                        className="filterInput"
                      />
                    </div>
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {displayedPendingUsers.map((user, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                      />
                    </td>
                    <td>{user.displayName}</td>
                    <td>{user.phone}</td>
                    <td>{user.card}</td>
                    <td>
                      {hasPermission('approve-users') && (
                        <button
                          onClick={() => handleApproveUser(user.id)}
                          disabled={user.status === 'approved'}
                          className={`${
                            user.status === 'approved' ? 'approvedButton' : ''
                          }`}
                        >
                          {user.status === 'approved' ? 'Approved' : 'Approve'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="bulkActions">
              {hasPermission('bulk-approve') && (
                <button
                  onClick={handleApproveSelectedUsers}
                  disabled={selectedUsers.length === 0}
                >
                  Approve Selected
                </button>
              )}
            </div>
            <div className="pagination">
              <button
                onClick={() =>
                  setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
                }
                disabled={currentPage === 1}
              >
                ←
              </button>
              <span>{currentPage}</span>
              <button
                onClick={() =>
                  setCurrentPage((prevPage) =>
                    Math.min(
                      prevPage + 1,
                      Math.ceil(pendingUsers.length / itemsPerPage)
                    )
                  )
                }
                disabled={
                  currentPage === Math.ceil(pendingUsers.length / itemsPerPage)
                }
              >
                →
              </button>
            </div>
          </div>
          {hasPermission('add-user') && (
            <div className="profileActions">
              <button onClick={handleAddUser} className="addUserButton">
                Add User
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
